var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { customElement, property } from 'lit/decorators.js';
import { html } from 'lit';
import { BwElement } from './base/bw-element.js';
const clamp01 = (n) => (n < 0 ? 0 : n > 1 ? 1 : n);
/** Default release time (ms) for smooth VU fall when `release` > 0 (review #7). */
export const VU_DEFAULT_RELEASE_MS = 3000;
/**
 * One ballistics step for the VU display level.
 * - Attack (rising, target ≥ current) is INSTANT — peaks show immediately.
 * - Release (falling) eases toward target linearly over `releaseMs` full-scale.
 * - releaseMs ≤ 0 → instant (no ballistics; the default value→fill contract).
 * Pure + deterministic (dt supplied) so it is unit-testable without rAF.
 */
export function vuDecayStep(current, target, dtMs, releaseMs) {
    if (releaseMs <= 0 || target >= current)
        return target;
    const next = current - dtMs / releaseMs;
    return next <= target ? target : next;
}
/** Number of LED segments in segmented decoration. */
export const BW_VU_SEGMENT_COUNT = 14;
/** Last green segment index (0-based). Indices 0..GREEN_LAST → green band. */
const GREEN_LAST = 8; // segments 0..8 → 9 green segments
/** Last amber segment index. Indices GREEN_LAST+1..AMBER_LAST → amber band. */
const AMBER_LAST = 9; // segment 9 → 1 amber segment (unity threshold)
// segments 10..13 → red band (clip zone)
let BwVuMeter = class BwVuMeter extends BwElement {
    constructor() {
        super(...arguments);
        this.size = 'md';
        this.decoration = 'plain';
        this.channels = '1';
        this.idle = false;
        this.value = 0; // 0..1 clamped, mono OR L
        this.valueR = 0; // 0..1 clamped, R when channels=2
        /**
         * Release time (ms) for smooth fall. 0 (default) = instant, preserving the
         * pure value→fill contract. Set e.g. 3000 for the ~3s VU decay (review #7):
         * attack stays instant, only the fall eases. Opt-in per meter so a consumer
         * whose engine already applies ballistics is not double-smoothed.
         */
        this.release = 0;
        this._targetL = 0;
        this._targetR = 0;
        this._dispL = 0;
        this._dispR = 0;
        this._raf = 0;
        this._lastTs = 0;
        this._tick = (ts) => {
            const dt = this._lastTs ? ts - this._lastTs : 16;
            this._lastTs = ts;
            this._dispL = vuDecayStep(this._dispL, this._targetL, dt, this.release);
            this._applyFill('l', this._dispL);
            let settled = this._dispL === this._targetL;
            if (this.channels === '2') {
                this._dispR = vuDecayStep(this._dispR, this._targetR, dt, this.release);
                this._applyFill('r', this._dispR);
                settled = settled && this._dispR === this._targetR;
            }
            this._raf = settled ? 0 : requestAnimationFrame(this._tick);
        };
    }
    _renderSegments() {
        const segs = [];
        for (let i = 0; i < BW_VU_SEGMENT_COUNT; i++) {
            const band = i <= GREEN_LAST ? 'green' : i <= AMBER_LAST ? 'amber' : 'red';
            segs.push(html `<span class="bw-vu-seg" data-band=${band}></span>`);
        }
        return segs;
    }
    render() {
        const isSeg = this.decoration === 'segmented';
        const bar = (cls) => isSeg
            ? html `<span class="bw-vu-meter-bar ${cls}">${this._renderSegments()}</span>`
            : html `<span class="bw-vu-meter-bar ${cls}"></span>`;
        return this.channels === '2'
            ? html `${bar('bw-vu-meter-bar-l')}${bar('bw-vu-meter-bar-r')}`
            : html `${bar('bw-vu-meter-bar-l')}`;
    }
    updated(changed) {
        if (changed.has('value') || changed.has('decoration') || changed.has('channels')) {
            this._targetL = clamp01(this.value);
            this._pump('l');
        }
        if (changed.has('valueR') || changed.has('decoration') || changed.has('channels')) {
            if (this.channels === '2') {
                this._targetR = clamp01(this.valueR);
                this._pump('r');
            }
        }
    }
    // Instant attack (sync apply); smooth release via rAF when release > 0 and falling.
    _pump(side) {
        const target = side === 'l' ? this._targetL : this._targetR;
        const disp = side === 'l' ? this._dispL : this._dispR;
        if (this.release <= 0 || target >= disp) {
            if (side === 'l')
                this._dispL = target;
            else
                this._dispR = target;
            this._applyFill(side, target);
        }
        else {
            this._ensureRaf();
        }
    }
    _ensureRaf() {
        if (this._raf)
            return;
        if (typeof requestAnimationFrame !== 'function') {
            // Headless (no rAF): fall straight to target so a meter never sticks lit.
            this._dispL = this._targetL;
            this._dispR = this._targetR;
            this._applyFill('l', this._dispL);
            if (this.channels === '2')
                this._applyFill('r', this._dispR);
            return;
        }
        this._lastTs = 0;
        this._raf = requestAnimationFrame(this._tick);
    }
    disconnectedCallback() {
        if (this._raf) {
            cancelAnimationFrame(this._raf);
            this._raf = 0;
        }
        super.disconnectedCallback();
    }
    _applyFill(side, raw) {
        const v = clamp01(raw);
        const pct = v * 100;
        this.style.setProperty(`--bw-vu-fill-${side}`, `${pct.toFixed(2)}%`);
        if (this.decoration !== 'segmented')
            return;
        // Light up integer segment count from left. Threshold: a segment is lit
        // when value >= (i+1)/N — i.e. segment 0 lights at >0, segment 13 at full.
        const lit = Math.min(BW_VU_SEGMENT_COUNT, Math.max(0, Math.ceil(v * BW_VU_SEGMENT_COUNT)));
        const sel = side === 'l' ? '.bw-vu-meter-bar-l' : '.bw-vu-meter-bar-r';
        const bar = this.querySelector(sel);
        if (!bar)
            return;
        const segs = bar.children;
        for (let i = 0; i < segs.length; i++) {
            const el = segs[i];
            el.classList.toggle('is-lit', i < lit);
        }
    }
};
__decorate([
    property({ type: String, reflect: true })
], BwVuMeter.prototype, "size", void 0);
__decorate([
    property({ type: String, reflect: true })
], BwVuMeter.prototype, "decoration", void 0);
__decorate([
    property({ type: String, reflect: true })
], BwVuMeter.prototype, "channels", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BwVuMeter.prototype, "idle", void 0);
__decorate([
    property({ type: Number })
], BwVuMeter.prototype, "value", void 0);
__decorate([
    property({ type: Number, attribute: 'value-r' })
], BwVuMeter.prototype, "valueR", void 0);
__decorate([
    property({ type: Number })
], BwVuMeter.prototype, "release", void 0);
BwVuMeter = __decorate([
    customElement('bw-vu-meter')
], BwVuMeter);
export { BwVuMeter };
//# sourceMappingURL=bw-vu-meter.js.map